import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ArrowLeft, Download, Share2, Monitor, Smartphone, Apple, MoreVertical, Menu, Plus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export function InstallGuidePage() {
  const navigate = useNavigate();
  const [detectedBrowser, setDetectedBrowser] = useState('chrome');
  const [isIOS, setIsIOS] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);

  useEffect(() => {
    const ua = navigator.userAgent.toLowerCase();
    
    // Detect platform
    const iOS = /ipad|iphone|ipod/.test(ua) && !window.MSStream;
    const android = /android/.test(ua);
    
    setIsIOS(iOS);
    setIsAndroid(android);
    setIsDesktop(!iOS && !android);

    // Detect browser
    if (/edg/.test(ua)) {
      setDetectedBrowser('edge');
    } else if (/chrome/.test(ua) && !/edg/.test(ua)) {
      setDetectedBrowser('chrome');
    } else if (/safari/.test(ua) && !/chrome/.test(ua)) {
      setDetectedBrowser('safari');
    } else if (/firefox/.test(ua)) {
      setDetectedBrowser('firefox');
    }
  }, []);

  const ChromeDesktopGuide = () => (
    <div className="space-y-6">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-800">
          <strong>Dica:</strong> O Chrome mostra o botão de instalação na barra de endereço!
        </p>
      </div>

      <div className="space-y-4">
        <div className="flex items-start gap-4 p-4 bg-white rounded-lg border">
          <div className="flex-shrink-0 w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center font-bold">1</div>
          <div>
            <h4 className="font-semibold">Procure o ícone de instalação</h4>
            <p className="text-muted-foreground text-sm mt-1">
              Na barra de endereço (URL), procure um ícone de <strong>monitor com seta</strong> ou <strong>+</strong> no lado direito.
            </p>
            <div className="mt-3 p-3 bg-gray-100 rounded-lg flex items-center gap-2">
              <div className="flex-1 bg-white rounded px-3 py-1 text-sm text-gray-500 border">
                ucleanapp.com.br
              </div>
              <div className="p-1.5 bg-gray-200 rounded hover:bg-gray-300 cursor-pointer">
                <Download className="h-4 w-4" />
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-start gap-4 p-4 bg-white rounded-lg border">
          <div className="flex-shrink-0 w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center font-bold">2</div>
          <div>
            <h4 className="font-semibold">Clique no ícone</h4>
            <p className="text-muted-foreground text-sm mt-1">
              Uma janela aparecerá perguntando se deseja instalar o "U-Clean".
            </p>
          </div>
        </div>

        <div className="flex items-start gap-4 p-4 bg-white rounded-lg border">
          <div className="flex-shrink-0 w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center font-bold">3</div>
          <div>
            <h4 className="font-semibold">Confirme a instalação</h4>
            <p className="text-muted-foreground text-sm mt-1">
              Clique em <strong>"Instalar"</strong>. O app será adicionado aos seus aplicativos.
            </p>
          </div>
        </div>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
        <p className="text-sm text-amber-800">
          <strong>Não encontrou o ícone?</strong> Clique nos <strong>três pontos (⋮)</strong> no canto superior direito do Chrome e procure por <strong>"Instalar U-Clean"</strong> ou <strong>"Instalar aplicativo"</strong>.
        </p>
      </div>
    </div>
  );

  const ChromeAndroidGuide = () => (
    <div className="space-y-6">
      <div className="space-y-4">
        <div className="flex items-start gap-4 p-4 bg-white rounded-lg border">
          <div className="flex-shrink-0 w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center font-bold">1</div>
          <div>
            <h4 className="font-semibold">Toque no menu (⋮)</h4>
            <p className="text-muted-foreground text-sm mt-1">
              No canto superior direito, toque nos <strong>três pontos verticais</strong>.
            </p>
            <div className="mt-3 flex justify-end">
              <div className="p-2 bg-gray-100 rounded">
                <MoreVertical className="h-5 w-5" />
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-start gap-4 p-4 bg-white rounded-lg border">
          <div className="flex-shrink-0 w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center font-bold">2</div>
          <div>
            <h4 className="font-semibold">Procure a opção de instalar</h4>
            <p className="text-muted-foreground text-sm mt-1">
              No menu, procure por:
            </p>
            <ul className="mt-2 space-y-1 text-sm">
              <li className="flex items-center gap-2">
                <Download className="h-4 w-4 text-green-600" />
                <span><strong>"Instalar aplicativo"</strong></span>
              </li>
              <li className="flex items-center gap-2">
                <Plus className="h-4 w-4 text-green-600" />
                <span>ou <strong>"Adicionar à tela inicial"</strong></span>
              </li>
            </ul>
          </div>
        </div>

        <div className="flex items-start gap-4 p-4 bg-white rounded-lg border">
          <div className="flex-shrink-0 w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center font-bold">3</div>
          <div>
            <h4 className="font-semibold">Confirme</h4>
            <p className="text-muted-foreground text-sm mt-1">
              Toque em <strong>"Instalar"</strong>. O ícone do U-Clean aparecerá na sua tela inicial!
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  const EdgeGuide = () => (
    <div className="space-y-6">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-800">
          O Edge tem uma opção dedicada para instalar apps!
        </p>
      </div>

      <div className="space-y-4">
        <div className="flex items-start gap-4 p-4 bg-white rounded-lg border">
          <div className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold">1</div>
          <div>
            <h4 className="font-semibold">Clique nos três pontos (...)</h4>
            <p className="text-muted-foreground text-sm mt-1">
              No canto superior direito do Edge, clique no menu <strong>"..."</strong>
            </p>
          </div>
        </div>

        <div className="flex items-start gap-4 p-4 bg-white rounded-lg border">
          <div className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold">2</div>
          <div>
            <h4 className="font-semibold">Vá em "Aplicativos"</h4>
            <p className="text-muted-foreground text-sm mt-1">
              No menu, procure a opção <strong>"Aplicativos"</strong> ou <strong>"Apps"</strong>.
            </p>
          </div>
        </div>

        <div className="flex items-start gap-4 p-4 bg-white rounded-lg border">
          <div className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold">3</div>
          <div>
            <h4 className="font-semibold">Instalar este site como aplicativo</h4>
            <p className="text-muted-foreground text-sm mt-1">
              Clique em <strong>"Instalar este site como um aplicativo"</strong> ou <strong>"Instalar U-Clean"</strong>.
            </p>
          </div>
        </div>

        <div className="flex items-start gap-4 p-4 bg-white rounded-lg border">
          <div className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold">4</div>
          <div>
            <h4 className="font-semibold">Confirme a instalação</h4>
            <p className="text-muted-foreground text-sm mt-1">
              Clique em <strong>"Instalar"</strong>. O app abrirá em uma janela própria!
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  const SafariIOSGuide = () => (
    <div className="space-y-6">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-800">
          <strong>Importante:</strong> No iPhone/iPad, você DEVE usar o Safari para instalar apps web!
        </p>
      </div>

      <div className="space-y-4">
        <div className="flex items-start gap-4 p-4 bg-white rounded-lg border">
          <div className="flex-shrink-0 w-8 h-8 bg-gray-800 text-white rounded-full flex items-center justify-center font-bold">1</div>
          <div>
            <h4 className="font-semibold">Toque no botão Compartilhar</h4>
            <p className="text-muted-foreground text-sm mt-1">
              Na barra inferior do Safari, toque no ícone de <strong>compartilhar</strong> (quadrado com seta para cima).
            </p>
            <div className="mt-3 flex justify-center">
              <div className="p-3 bg-gray-100 rounded-lg">
                <Share2 className="h-8 w-8 text-blue-500" />
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-start gap-4 p-4 bg-white rounded-lg border">
          <div className="flex-shrink-0 w-8 h-8 bg-gray-800 text-white rounded-full flex items-center justify-center font-bold">2</div>
          <div>
            <h4 className="font-semibold">Role para baixo no menu</h4>
            <p className="text-muted-foreground text-sm mt-1">
              No menu que aparece, <strong>role para baixo</strong> até encontrar mais opções.
            </p>
          </div>
        </div>

        <div className="flex items-start gap-4 p-4 bg-white rounded-lg border">
          <div className="flex-shrink-0 w-8 h-8 bg-gray-800 text-white rounded-full flex items-center justify-center font-bold">3</div>
          <div>
            <h4 className="font-semibold">Toque em "Adicionar à Tela de Início"</h4>
            <p className="text-muted-foreground text-sm mt-1">
              Procure a opção <strong>"Adicionar à Tela de Início"</strong> (ou "Add to Home Screen" em inglês).
            </p>
            <div className="mt-3 p-3 bg-gray-100 rounded-lg flex items-center gap-3">
              <div className="p-2 bg-white rounded">
                <Plus className="h-5 w-5" />
              </div>
              <span className="font-medium">Adicionar à Tela de Início</span>
            </div>
          </div>
        </div>

        <div className="flex items-start gap-4 p-4 bg-white rounded-lg border">
          <div className="flex-shrink-0 w-8 h-8 bg-gray-800 text-white rounded-full flex items-center justify-center font-bold">4</div>
          <div>
            <h4 className="font-semibold">Confirme tocando em "Adicionar"</h4>
            <p className="text-muted-foreground text-sm mt-1">
              No canto superior direito, toque em <strong>"Adicionar"</strong>. Pronto! O U-Clean estará na sua tela inicial.
            </p>
          </div>
        </div>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
        <p className="text-sm text-amber-800">
          <strong>Não encontrou a opção?</strong> Certifique-se de que está usando o <strong>Safari</strong> (não Chrome ou outro navegador). No iOS, apenas o Safari suporta instalação de apps web.
        </p>
      </div>
    </div>
  );

  const SafariMacGuide = () => (
    <div className="space-y-6">
      <div className="space-y-4">
        <div className="flex items-start gap-4 p-4 bg-white rounded-lg border">
          <div className="flex-shrink-0 w-8 h-8 bg-gray-800 text-white rounded-full flex items-center justify-center font-bold">1</div>
          <div>
            <h4 className="font-semibold">Clique em "Arquivo" no menu</h4>
            <p className="text-muted-foreground text-sm mt-1">
              Na barra de menu do Safari (topo da tela), clique em <strong>"Arquivo"</strong>.
            </p>
          </div>
        </div>

        <div className="flex items-start gap-4 p-4 bg-white rounded-lg border">
          <div className="flex-shrink-0 w-8 h-8 bg-gray-800 text-white rounded-full flex items-center justify-center font-bold">2</div>
          <div>
            <h4 className="font-semibold">Adicionar ao Dock</h4>
            <p className="text-muted-foreground text-sm mt-1">
              Procure a opção <strong>"Adicionar ao Dock"</strong> ou <strong>"Add to Dock"</strong>.
            </p>
          </div>
        </div>

        <div className="flex items-start gap-4 p-4 bg-white rounded-lg border">
          <div className="flex-shrink-0 w-8 h-8 bg-gray-800 text-white rounded-full flex items-center justify-center font-bold">3</div>
          <div>
            <h4 className="font-semibold">Confirme</h4>
            <p className="text-muted-foreground text-sm mt-1">
              O U-Clean aparecerá no seu Dock como um app nativo!
            </p>
          </div>
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-800">
          <strong>macOS Sonoma ou mais recente:</strong> O Safari 17+ suporta instalação completa de Web Apps com ícone no Dock e Launchpad.
        </p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="container max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex items-center gap-2">
            <Download className="h-6 w-6 text-green-600" />
            <span className="font-bold text-xl">Instalar U-Clean</span>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="container max-w-4xl mx-auto px-4 py-8">
        {/* Hero */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-2xl mb-4">
            <Smartphone className="h-10 w-10 text-green-600" />
          </div>
          <h1 className="text-3xl font-bold mb-2">Instale o App U-Clean</h1>
          <p className="text-muted-foreground max-w-md mx-auto">
            Tenha acesso rápido, funcione offline e receba notificações. É grátis e não ocupa espaço!
          </p>
        </div>

        {/* Device Detection */}
        <Card className="mb-6 border-green-200 bg-green-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              {isIOS && <Apple className="h-6 w-6" />}
              {isAndroid && <Smartphone className="h-6 w-6" />}
              {isDesktop && <Monitor className="h-6 w-6" />}
              <div>
                <p className="font-medium">
                  Detectamos que você está usando: 
                  <span className="text-green-700 ml-1">
                    {isIOS && 'iPhone/iPad'}
                    {isAndroid && 'Android'}
                    {isDesktop && 'Computador'}
                  </span>
                </p>
                <p className="text-sm text-muted-foreground">
                  Navegador: <span className="capitalize">{detectedBrowser}</span>
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Instructions by Browser */}
        <Card>
          <CardHeader>
            <CardTitle>Passo a Passo</CardTitle>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue={isIOS ? 'safari-ios' : detectedBrowser} className="w-full">
              <TabsList className="grid w-full grid-cols-2 md:grid-cols-4 mb-6">
                <TabsTrigger value="chrome" className="text-xs md:text-sm">
                  Chrome
                </TabsTrigger>
                <TabsTrigger value="edge" className="text-xs md:text-sm">
                  Edge
                </TabsTrigger>
                <TabsTrigger value="safari-ios" className="text-xs md:text-sm">
                  Safari (iOS)
                </TabsTrigger>
                <TabsTrigger value="safari-mac" className="text-xs md:text-sm">
                  Safari (Mac)
                </TabsTrigger>
              </TabsList>

              <TabsContent value="chrome">
                {isAndroid ? <ChromeAndroidGuide /> : <ChromeDesktopGuide />}
              </TabsContent>

              <TabsContent value="edge">
                <EdgeGuide />
              </TabsContent>

              <TabsContent value="safari-ios">
                <SafariIOSGuide />
              </TabsContent>

              <TabsContent value="safari-mac">
                <SafariMacGuide />
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        {/* Benefits */}
        <Card className="mt-6">
          <CardHeader>
            <CardTitle>Por que instalar?</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="p-4 bg-blue-50 rounded-lg text-center">
                <div className="text-3xl mb-2">⚡</div>
                <h4 className="font-semibold">Acesso Rápido</h4>
                <p className="text-sm text-muted-foreground">Abra direto da tela inicial, sem digitar URL</p>
              </div>
              <div className="p-4 bg-green-50 rounded-lg text-center">
                <div className="text-3xl mb-2">📴</div>
                <h4 className="font-semibold">Funciona Offline</h4>
                <p className="text-sm text-muted-foreground">Acesse conteúdo mesmo sem internet</p>
              </div>
              <div className="p-4 bg-purple-50 rounded-lg text-center">
                <div className="text-3xl mb-2">🔔</div>
                <h4 className="font-semibold">Notificações</h4>
                <p className="text-sm text-muted-foreground">Receba lembretes dos seus check-ins</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* FAQ */}
        <Card className="mt-6">
          <CardHeader>
            <CardTitle>Perguntas Frequentes</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <h4 className="font-semibold">O app ocupa espaço no celular?</h4>
              <p className="text-sm text-muted-foreground">
                Quase nenhum! Diferente de apps tradicionais, o U-Clean usa a tecnologia PWA que ocupa menos de 5MB.
              </p>
            </div>
            <div>
              <h4 className="font-semibold">É seguro instalar?</h4>
              <p className="text-sm text-muted-foreground">
                Sim! O PWA é apenas um atalho para o site, com recursos extras. Não tem acesso a dados do seu celular além do que você permitir.
              </p>
            </div>
            <div>
              <h4 className="font-semibold">Como desinstalar?</h4>
              <p className="text-sm text-muted-foreground">
                Da mesma forma que qualquer app: segure o ícone e escolha "Remover" ou "Desinstalar".
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Back button */}
        <div className="mt-8 text-center">
          <Button variant="outline" onClick={() => navigate(-1)}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Voltar ao App
          </Button>
        </div>
      </main>
    </div>
  );
}
